import { useRouter } from "next/router";
import { useEffect, useState } from "react";
import {
  alert,
  getPointingSystem,
  getRestaurantInfo,
} from "../../utils/APICalls/APICalls";
import { getCookie } from "../../utils/CookieHelper";
import { COOKIE_ENUM, PagesEnum } from "../../utils/enums";
import envConfig from "../../utils/env.config";
import { AlertErrorType } from "../../utils/ErrorTypes";
import { getAllDisabledItems } from "../../utils/help/itemDetails/itemDetails";
import { getPageLink } from "../../utils/help/navigation/navigation";
import { parseJwt } from "../../utils/help/user/user";
import { createOffer } from "../../utils/OfferClass/OfferClassUtils/OfferCreator/offerCreator";
import {
  RESTAURANT_IMAGE_SOURCE,
  Restaurant,
  RestaurantPackageOffer,
} from "../../utils/types";
import { getRestaurantSocialMedia } from "../redux/http/getRestaurantSocialMedia";
import {
  setHasRoundingMethodAndLevel,
  setPointingSystem,
} from "../redux/slices/cartAndLoyaltySlice";
import { setAllRestaurantDetailsInRedux } from "../redux/slices/configSlice";
import {
  sendGetPointingRequest,
  setCalledRestaurantInfoRequest,
} from "../redux/slices/requestLimiterSlice";
import { useAppDispatch, useAppSelector } from "./useReduxHooks";

const useRestaurantData = () => {
  const dispatch = useAppDispatch();
  const router = useRouter();
  const franchiseSlug = router.query.franchise as string;
  const restaurantInfo = useAppSelector((state) => state.config.restaurantInfo);
  const restaurantConfig = useAppSelector(
    (state) => state.config.restaurantConfig
  );
  const requestLimiter = useAppSelector((state) => state.requestLimiter);

  const token = useAppSelector((state) => state.userInfo.token);
  const itemStatusRedux = useAppSelector((state) => state.itemStatus);
  const selectedFranchise = useAppSelector(
    (state) => state.config.selectedFranchise
  );

  const customerShortId = useAppSelector(
    (state) => state.userInfo.customerShortId
  );

  const orderTypeParam = useAppSelector(
    (state) => state.queryParams.orderTypeParam
  );

  const isRestaurantSwitchInProgress = useAppSelector(
    (state) => state.config.isRestaurantSwitchInProgress
  );

  const [alertSent, setAlertSent] = useState(false);

  // Reset the restaurant info request flag when franchise changes
  useEffect(() => {
    dispatch(setCalledRestaurantInfoRequest(false));
  }, [selectedFranchise?.restaurantId]);

  const getAndSetPointingSystem = async (restaurantId: number) => {
    const allDisabledItemsIds = getAllDisabledItems(
      itemStatusRedux,
      orderTypeParam
    );

    await getPointingSystem(
      restaurantId,
      true,
      restaurantConfig?.hasOfflineMode ? customerShortId : undefined
    )
      .then((res) => {
        if (res.data) {
          res.data.restaurantPackageOffers = res.data.restaurantPackageOffers
            .map((restaurantPackageOffer: RestaurantPackageOffer) => {
              return {
                ...restaurantPackageOffer,
                packageOffer: {
                  ...restaurantPackageOffer.packageOffer,
                  isCartV2: true,
                  milestonePoints:
                    restaurantPackageOffer.pointingMilestone?.points,
                },
              };
            })
            .filter((restaurantPackageOffer: RestaurantPackageOffer) => {
              const offerV2 = createOffer(restaurantPackageOffer.packageOffer);

              return !offerV2.containsDisabledItems(allDisabledItemsIds);
            });
          dispatch(setPointingSystem(res.data));
        } else {
          dispatch(setPointingSystem(undefined));
        }
      })
      .catch((e) => {
        console.log("error getting point system ", e);
      });
  };

  useEffect(() => {
    if (restaurantInfo) {
      if (franchiseSlug) {
        const currentFranchise = restaurantInfo.franchises.find(
          (fr) => fr.slug === franchiseSlug
        );
        if (!currentFranchise) {
          console.log("router push 12th", router, PagesEnum.Home);
          router.push(`${getPageLink(router, PagesEnum.Home).as}`);
        }
      }
      const hasRoundingMethodAndLevel =
        !!restaurantInfo.currencyRoundingMethod &&
        !!restaurantInfo.currencyRoundingLevel;
      dispatch(setHasRoundingMethodAndLevel(hasRoundingMethodAndLevel));
    }
    if (
      window.location.hostname.includes(".prepit.") &&
      restaurantInfo?.customDomain
    ) {
      const customDomain = `${window.location.protocol}//${restaurantInfo.customDomain}${window.location.pathname}${window.location.search}`;
      router.push(customDomain);
    }
  }, [restaurantInfo?.id]);

  useEffect(() => {
    const restaurantId =
      restaurantInfo?.id || parseInt(envConfig.restaurantId!);
    if (token) {
      dispatch(getRestaurantSocialMedia(restaurantId));
    }
  }, [token]);

  useEffect(() => {
    if (!requestLimiter.calledRestaurantInfoRequest) {
      const currentRestaurantId = restaurantInfo?.id;
      const expectedRestaurantId = selectedFranchise?.restaurantId;

      const restaurantId =
        expectedRestaurantId?.toString() ||
        currentRestaurantId?.toString() ||
        envConfig.restaurantId!;

      getRestaurantInfo(restaurantId).then((res) => {
        const restaurantData: Restaurant = res.data;
        // filter restaurant images
        restaurantData.restaurantImages =
          restaurantData.restaurantImages?.filter(
            (image) =>
              !image.source || image.source === RESTAURANT_IMAGE_SOURCE.WEB
          );

        dispatch(setAllRestaurantDetailsInRedux(restaurantData));
        const hasRoundingMethodAndLevel =
          !!restaurantData?.currencyRoundingMethod &&
          !!restaurantData?.currencyRoundingLevel;
        dispatch(setHasRoundingMethodAndLevel(hasRoundingMethodAndLevel));
      });
    }

    return () => {
      dispatch(setCalledRestaurantInfoRequest(true));
    };
  }, [
    restaurantInfo?.id,
    isRestaurantSwitchInProgress,
    selectedFranchise?.restaurantId,
  ]);

  useEffect(() => {
    const expectedRestaurantId = selectedFranchise?.restaurantId;
    const currentRestaurantId = restaurantInfo?.id;

    const restaurantId =
      expectedRestaurantId ||
      currentRestaurantId ||
      parseInt(envConfig.restaurantId!);

    if (getCookie(COOKIE_ENUM.TOKEN)) {
      getAndSetPointingSystem(restaurantId);
      dispatch(sendGetPointingRequest());
      const now = new Date();
      if (
        !alertSent &&
        requestLimiter.getPointingRequests.count > 10 &&
        Math.abs(
          now.valueOf() - requestLimiter.getPointingRequests.lastCalledAt
        ) /
          (1000 * 60) <
          1
      ) {
        const decodedToken = parseJwt(token);
        alert(`${AlertErrorType.GetPointing_Called_More_than_10_times}`, {
          customerId: decodedToken?.customer_id,
          token,
        });
        setAlertSent(true);
      }
    }
  }, [
    token,
    restaurantInfo?.id,
    selectedFranchise?.restaurantId,
    itemStatusRedux.disabledItemsIds.length,
    orderTypeParam,
  ]);
};

export default useRestaurantData;
